import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ApiTokensSection } from "@/components/ApiTokensSection";

export function UserManagementSection() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "sales", currency: "CAD", coefficient: "1", price_per_skid: "0" });
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", password: "", name: "", role: "sales", currency: "CAD", coefficient: "1", price_per_skid: "0" });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast({
        title: "Failed to load users",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", {
        ...form,
        coefficient: parseFloat(form.coefficient) || 1,
        price_per_skid: parseFloat(form.price_per_skid) || 0,
      });
      toast({ title: "User created" });
      setAddOpen(false);
      setForm({ email: "", password: "", name: "", role: "sales", currency: "CAD", coefficient: "1", price_per_skid: "0" });
      fetchUsers();
    } catch (e) {
      toast({
        title: "Failed to create user",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role,
      currency: u.currency || "CAD",
      coefficient: String(u.coefficient ?? 1),
      price_per_skid: String(u.price_per_skid ?? 0),
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        email: editForm.email,
        name: editForm.name,
        role: editForm.role,
        currency: editForm.currency,
        coefficient: parseFloat(editForm.coefficient) || 1,
        price_per_skid: parseFloat(editForm.price_per_skid) || 0,
      };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editTarget.id}`, payload);
      toast({ title: "User updated" });
      setEditTarget(null);
      fetchUsers();
    } catch (e) {
      toast({
        title: "Failed to update user",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast({ title: "User deleted" });
      setDeleteTarget(null);
      fetchUsers();
    } catch (e) {
      toast({
        title: "Failed to delete user",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
      setDeleteTarget(null);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="settings-users-section">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-zinc-950">User Accounts</h3>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
              data-testid="add-user-button"
            >
              <Plus size={14} className="mr-1.5" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">New User Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-sm border-zinc-300"
                  data-testid="add-user-name-input"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Email</Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-sm border-zinc-300"
                  data-testid="add-user-email-input"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Password</Label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="rounded-sm border-zinc-300"
                  data-testid="add-user-password-input"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="add-user-role-select">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="gt_sales">GT Sales</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "customer" && (
                <>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Currency</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger className="rounded-sm border-zinc-300" data-testid="add-user-currency-select">
                        <SelectValue placeholder="Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CAD">CAD</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="RMB">RMB</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Price Coefficient</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form.coefficient}
                      onChange={(e) => setForm({ ...form, coefficient: e.target.value })}
                      className="rounded-sm border-zinc-300 font-mono-data"
                      data-testid="add-user-coefficient-input"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Price/Skids (Inland Freight)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.price_per_skid}
                      onChange={(e) => setForm({ ...form, price_per_skid: e.target.value })}
                      className="rounded-sm border-zinc-300 font-mono-data"
                      data-testid="add-user-price-per-skid-input"
                    />
                  </div>
                </>
              )}
              <DialogFooter className="mt-2">
                <Button
                  type="submit"
                  disabled={saving}
                  className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
                  data-testid="add-user-submit-button"
                >
                  {saving ? "Creating..." : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <Table className="min-w-[600px]" data-testid="user-management-table">
            <TableHeader>
              <TableRow className="border-b border-zinc-300">
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-b border-zinc-200" data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm">{u.name || "-"}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell className="text-sm capitalize">{u.role === "gt_sales" ? "GT Sales" : u.role}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm border-zinc-300"
                        onClick={() => openEdit(u)}
                        data-testid={`edit-user-button-${u.id}`}
                      >
                        <PencilSimple size={14} className="mr-1.5" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={u.id === user?.id}
                        className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        onClick={() => setDeleteTarget(u)}
                        data-testid={`delete-user-button-${u.id}`}
                      >
                        <Trash size={14} className="mr-1.5" /> Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {user?.role === "admin" && <ApiTokensSection />}

      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User: {editTarget?.email}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="rounded-sm border-zinc-300"
                data-testid="edit-user-name-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Email</Label>
              <Input
                type="email"
                required
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="rounded-sm border-zinc-300"
                data-testid="edit-user-email-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">
                New Password (leave blank to keep unchanged)
              </Label>
              <Input
                type="password"
                minLength={6}
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                className="rounded-sm border-zinc-300"
                data-testid="edit-user-password-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                disabled={editTarget?.id === user?.id}
              >
                <SelectTrigger className="rounded-sm border-zinc-300" data-testid="edit-user-role-select">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="gt_sales">GT Sales</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.role === "customer" && (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Currency</Label>
                  <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="edit-user-currency-select">
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CAD">CAD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="RMB">RMB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Price Coefficient</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editForm.coefficient}
                    onChange={(e) => setEditForm({ ...editForm, coefficient: e.target.value })}
                    className="rounded-sm border-zinc-300 font-mono-data"
                    data-testid="edit-user-coefficient-input"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Price/Skids (Inland Freight)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price_per_skid}
                    onChange={(e) => setEditForm({ ...editForm, price_per_skid: e.target.value })}
                    className="rounded-sm border-zinc-300 font-mono-data"
                    data-testid="edit-user-price-per-skid-input"
                  />
                </div>
              </>
            )}
            <DialogFooter className="mt-2">
              <Button
                type="submit"
                disabled={saving}
                className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
                data-testid="edit-user-submit-button"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This user will no longer be able to log in to the dashboard. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              data-testid="delete-user-confirm-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
